import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Chip, IconButton, Menu, MenuItem,
  ListItemIcon, ListItemText, Stack, alpha, useTheme, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, ButtonBase,
} from '@mui/material';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import { useApp } from '../contexts/AppContext';
import { useQuiz } from '../contexts/QuizContext';
import { getStats } from '../utils';
import { QUESTION_TYPE_LABELS } from '../types';
import type { QuizBank } from '../types';

type ModeOption = 'default' | 'exam' | 'homework' | 'halfExam';
const MODE_LABEL: Record<ModeOption, string> = { default: '默认', exam: '考试', homework: '作业', halfExam: '半考试' };

export default function Home() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { state, removeBank } = useApp();
  const { session, clearSession, startQuiz } = useQuiz();
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement | null; bankId: string | null }>({ el: null, bankId: null });
  const [pendingBank, setPendingBank] = useState<QuizBank | null>(null);
  const [modeMenuAnchor, setModeMenuAnchor] = useState<HTMLElement | null>(null);
  const [quickMode, setQuickMode] = useState<ModeOption>('default');
  const hasActiveSession = !!(session && !session.isFinished);

  const startWithBank = (bank: QuizBank) => {
    const count = state.settings.defaultQuickCount;
    setPendingBank(null);
    startQuiz(bank, count, undefined, quickMode === 'default' ? undefined : quickMode);
    navigate('/quiz/active');
  };

  const handleCardClick = (bank: QuizBank) => {
    if (hasActiveSession) { setPendingBank(bank); return; }
    startWithBank(bank);
  };

  const handleRemove = (bankId: string) => {
    setMenuAnchor({ el: null, bankId: null });
    removeBank(bankId);
  };

  const totalQuestions = state.banks.reduce((s, b) => s + b.hqb.questions.length, 0);

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
      {/* Hero */}
      <Stack direction="row" sx={{ mb: 3, alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 2, display: 'block', mb: 0.5, fontWeight: 600, fontSize: '0.7rem' }}>
            禾云工作室
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.15, mb: 0.5 }}>
            刷题助手
          </Typography>
          <Typography variant="body2" color="text.secondary">
            共 {state.banks.length} 个题库 · {totalQuestions} 道题目
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Chip
            icon={<TuneRoundedIcon sx={{ fontSize: 16 }} />} label="自定义刷题"
            onClick={() => navigate('/custom')}
            variant="outlined"
            sx={{ borderRadius: 3, fontWeight: 600, py: 2, px: 1, fontSize: '0.75rem' }}
          />
        </Stack>
      </Stack>

      {/* Mode selection */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>快速刷题模式：</Typography>
          <ButtonBase
            onClick={e => setModeMenuAnchor(e.currentTarget)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              px: 2, py: 0.75, borderRadius: 3,
              color: 'primary.main', fontWeight: 600, fontSize: '0.85rem',
            }}
          >
            {MODE_LABEL[quickMode]}<ArrowDropDownRoundedIcon fontSize="small" />
          </ButtonBase>
        </Box>
      </Box>

      {/* Mode menu */}
      <Menu
        anchorEl={modeMenuAnchor}
        open={Boolean(modeMenuAnchor)}
        onClose={() => setModeMenuAnchor(null)}
        slotProps={{ paper: { sx: { borderRadius: 3, minWidth: 160, mt: 1 } } }}
      >
        {(Object.entries(MODE_LABEL) as [ModeOption, string][]).map(([k, v]) => (
          <MenuItem key={k} selected={quickMode === k} onClick={() => { setQuickMode(k); setModeMenuAnchor(null); }}>
            <ListItemText primary={v} />
          </MenuItem>
        ))}
      </Menu>

      {/* Empty state */}
      {state.banks.length === 0 && (
        <Card
          elevation={0}
          sx={{
            p: 5, textAlign: 'center',
            bgcolor: 'background.paper', border: '1px dashed', borderColor: 'divider', borderRadius: 4,
          }}
        >
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>还没有题库</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            前往设置页面上传 .hqb 格式题库文件，或等待内置题库加载
          </Typography>
        </Card>
      )}

      {/* Bank list — clicking card starts quiz */}
      <Stack spacing={2}>
        {state.banks.map(bank => {
          const stats = getStats(bank);
          return (
            <Card
              key={bank.id}
              elevation={0}
              onClick={() => handleCardClick(bank)}
              sx={{
                borderRadius: 4, cursor: 'pointer',
                bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                transition: 'all 0.15s ease',
                '&:active': { transform: 'scale(0.99)' },
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.8} sx={{ mb: 1 }}>
                      <Chip
                        size="small"
                        label={bank.source === 'builtin' ? '内置' : '自定义'}
                        sx={{ height: 24, fontSize: '0.7rem', fontWeight: 600 }}
                        color={bank.source === 'builtin' ? 'secondary' : 'primary'}
                        variant="outlined"
                      />
                      <Chip size="small" label={`${stats.total} 题`} sx={{ height: 24, fontSize: '0.7rem', bgcolor: alpha(theme.palette.primary.main, 0.08) }} />
                    </Stack>

                    <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, mb: 0.5 }}>
                      {bank.name}
                    </Typography>

                    {bank.hqb.metadata.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {bank.hqb.metadata.description}
                      </Typography>
                    )}

                    <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                      {(['single', 'multiple', 'judge'] as const)
                        .filter(t => stats[t] > 0)
                        .map(t => (
                          <Chip key={t} size="small" label={`${QUESTION_TYPE_LABELS[t]} ${stats[t]}`} variant="outlined" sx={{ height: 24, fontSize: '0.7rem' }} />
                        ))}
                    </Stack>
                  </Box>

                  <IconButton size="small" onClick={e => { e.stopPropagation(); setMenuAnchor({ el: e.currentTarget, bankId: bank.id }); }}>
                    <MoreVertRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* Context menu */}
      <Menu
        anchorEl={menuAnchor.el}
        open={Boolean(menuAnchor.el)}
        onClose={() => setMenuAnchor({ el: null, bankId: null })}
        slotProps={{ paper: { sx: { borderRadius: 3, minWidth: 160, mt: 1 } } }}
      >
        <MenuItem onClick={() => menuAnchor.bankId && handleRemove(menuAnchor.bankId)}>
          <ListItemIcon><DeleteOutlineRoundedIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primary="移除题库" slotProps={{ primary: { color: 'error' } }} />
        </MenuItem>
      </Menu>

      {/* Session conflict dialog */}
      <Dialog open={!!pendingBank} onClose={() => setPendingBank(null)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { borderRadius: 4 } } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>已有进行中的刷题</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            当前有一个尚未结束的刷题会话。请在下方选择操作以继续刷题
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => { setPendingBank(null); navigate('/quiz/active'); }} variant="outlined" sx={{ borderRadius: 2.5 }}>
            前往当前刷题会话
          </Button>
          <Button
            variant="contained" color="error" sx={{ borderRadius: 2.5 }}
            onClick={() => {
              const bank = pendingBank;
              setPendingBank(null);
              clearSession();
              if (bank) startWithBank(bank);
            }}
          >
            结束并继续本次刷题
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
